// A jwt_bearer grant's private key read from PEM: the contract's check of
// the key the Curator API takes, and the form the EgressGateway imports.
// Part of the Curator API contract, so it imports only effect. See Design >
// Exchange, "Curator API".

import { Option, Result } from "effect";
import { Base64 } from "effect/encoding";

// A jwt_bearer grant's private key, read from PEM as PKCS#8 DER, the form
// WebCrypto imports, with the kind of key it is: PKCS#8 ("PRIVATE KEY") as
// it is, and PKCS#1 RSA ("RSA PRIVATE KEY", as GitHub gives it) or SEC1 EC
// on P-256 ("EC PRIVATE KEY") wrapped as PKCS#8. None for anything else,
// such as an encrypted key or another curve. Only the key's structure is
// read here; the EgressGateway's import checks the rest. See Design >
// Toolsets and tools, "EgressGateway".
const RSA_OID = [0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01];
const EC_OID = [0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01];
const P256_OID = [0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07];
const holds = (der: Uint8Array, part: readonly number[]) => {
  for (let i = 0; i + part.length <= der.length; i++) if (part.every((b, j) => der[i + j] === b)) return true;
  return false;
};
const lengthOf = (n: number) => n < 0x80 ? [n] : n < 0x100 ? [0x81, n] : [0x82, n >> 8, n & 0xff];
const tlv = (tag: number, parts: readonly (readonly number[] | Uint8Array)[]) => {
  const body = parts.flatMap((p) => [...p]);
  return Uint8Array.from([tag, ...lengthOf(body.length), ...body]);
};
// PKCS#8: SEQUENCE { version 0, AlgorithmIdentifier, OCTET STRING { key } } (RFC 5208).
const pkcs8Of = (algorithm: readonly number[], key: Uint8Array) => tlv(0x30, [[0x02, 0x01, 0x00], tlv(0x30, [algorithm]), tlv(0x04, [key])]);
// Each PEM label's key, as PKCS#8 with its kind, or None.
type PemKey = Option.Option<{ readonly der: Uint8Array; readonly kind: "rsa" | "ec" }>;
const PEM_LABELS = {
  "RSA PRIVATE KEY": (key) => Option.some({ der: pkcs8Of([...RSA_OID, 0x05, 0x00], key), kind: "rsa" }),
  "EC PRIVATE KEY": (key) => holds(key, P256_OID) ? Option.some({ der: pkcs8Of([...EC_OID, ...P256_OID], key), kind: "ec" }) : Option.none(),
  "PRIVATE KEY": (key) => holds(key, RSA_OID) ? Option.some({ der: key, kind: "rsa" })
    : holds(key, EC_OID) && holds(key, P256_OID) ? Option.some({ der: key, kind: "ec" }) : Option.none(),
} satisfies Record<string, (key: Uint8Array) => PemKey>;
const isPemLabel = (label: string): label is keyof typeof PEM_LABELS => Object.hasOwn(PEM_LABELS, label);
const PEM = /^-----BEGIN (PRIVATE KEY|RSA PRIVATE KEY|EC PRIVATE KEY)-----\r?\n([A-Za-z0-9+/=\r\n]+?)\r?\n?-----END \1-----\s*$/;
export const pemKeyOf = (pem: string): PemKey => {
  const found = PEM.exec(pem.trim());
  const label = found?.[1] ?? "";
  if (found === null || !isPemLabel(label)) return Option.none();
  const der = Base64.decode((found[2] ?? "").replace(/\s+/g, ""));
  if (Result.isFailure(der) || der.success.length < 16 || der.success.length > 8192) return Option.none();
  return PEM_LABELS[label](der.success);
};
// The kind of key each algorithm signs with.
export const KEY_KIND_OF_ALG = { RS256: "rsa", ES256: "ec" } as const;
// How WebCrypto imports each algorithm's private key, as PKCS#8.
export const IMPORT_OF_ALG = {
  RS256: { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
  ES256: { name: "ECDSA", namedCurve: "P-256" },
} as const;
